
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import vSelect3 from "vselect3";
import { EventDetails } from "@/store/site-monitor/types";
import { EventOutcome, EventOutcomeResponse } from "@/types/EventOutcome";
import { UserPermissions } from "@/store/types";
import NestedSelect from "@/components/utilities/NestedSelect.vue";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import UpdateEventOutcomeRequest from "@/types/sv-data/requests/UpdateEventOutcome";
import api from "@/services/api.service";
import { cloneDeep } from 'lodash';
import VirtualAssistSummary from '../ai-virtual-operator/VirtualAssistSummary.vue';

const SiteMonitor = namespace("siteMonitor");
const Tasks = namespace("tasks");

@Component({
	components: {
		"v-select-3": vSelect3,
		"nested-select": NestedSelect,
		"virtual-assist-summary": VirtualAssistSummary
	}
})
export default class SetEventOutcomeAndNote extends Vue {
	@Prop(Number)
	private eventId?: number;

	@Prop({type: Array, required: false})
	private eventOutcomePath: EventOutcome[];

	@Prop({ type: String, required: false })
	private eventNote: string;

	@Prop({ type: Boolean, required: false, default: false})
	private eventOutcomeDisabled: boolean;

	@Prop({ type: Boolean, required: false, default: false})
	private eventNoteDisabled: boolean;

	@Prop({ type: Boolean, required: false, default: false})
	private required: boolean;

	@Getter getFeature: (featuresList: string[]) => boolean;
	@Getter getPermissions: UserPermissions;
	@SiteMonitor.Getter getEventDetails: EventDetails;
	@SiteMonitor.Getter("eventOutcomesFlatList") private outcomesFlatList: EventOutcome[];
	@SiteMonitor.Mutation setEventDetails: any;
	@SiteMonitor.Mutation resetEventRecords: () => void;
	@SiteMonitor.Action("loadOutcomesForEvent") loadOutcomes: (eventId: number) => Promise<EventOutcomeResponse>;
	@SiteMonitor.Action private fetchEventDetails: (eventId: number) => Promise<EventDetails>;
	@Tasks.Getter hasEventOutcomeTask: boolean;

	private outcomeTree: any[] = [];
	private outcomeSelectionPath: EventOutcome[] = [];
	private outcomeSelectionValid: boolean = true;
	private outcomeNote: string = "";

	private hasUserManualChange: boolean = false;
	private eventOutcomeChangedTooltip: string = "This event's outcome has changed whilst a notification rule has been applied, click this to update the Outcome/Notification Rules.";

	public async submit(): Promise<void> {
		if (!this.isOutcomeNoteValid || !this.outcomeSelectionValid) {
			throw Error("Unable to update event outcome. Outcome or note is invalid");
		}
		try {
			let payload: UpdateEventOutcomeRequest = {
				eventId: this.eventId,
				eventOutcomeId: this.eventOutcome!.eventOutcomeID,
				note: this.outcomeNote
			};
			await api.updateEventOutcomeByNotification(payload);
			
			// Reset the flag after successful update
			this.hasUserManualChange = false;
		} catch (err) {
			console.error(err);

			throw Error("Unable to update event outcome, please try again later. If the problem persists, contact your support provider.");
		}
	}

	private async mounted(): Promise<void> {
		this.hasUserManualChange = false;

		// Get up-to-date Event details
		await this.fetchEventDetails(this.eventId);

		// Load outcomes and set selected outcome path if exists
		await this.loadOutcomesForEvent();

		// Set the note from event details if not provided via props
		this.setOutcomeNote();
	}

	private async loadOutcomesForEvent(): Promise<void> {
		let response = await this.loadOutcomes(this.eventId);
		this.outcomeTree = response.eventOutcomeTree;

		if (!!this.eventOutcomePath && this.eventOutcomePath.length > 0) {
			this.outcomeSelectionPath = cloneDeep(this.eventOutcomePath);
		} else {
			// Reset the selection path and build it from event details
			this.outcomeSelectionPath = [];
			this.buildOutcomePath();
		}
	}

	private get isEventOutcomeDifferent(): boolean {
		// If the event outcome ID from event details is different from the last ID in the outcome selection path,
		// And the user has not manually changed the selection
		return this.getEventDetails?.eventOutcomeID !== null && this.eventOutcomePath === null
			|| (
				this.eventOutcomePath && this.eventOutcomePath.length > 0 
				&& this.eventOutcomePath[this.eventOutcomePath.length - 1]?.eventOutcomeID !== this.getEventDetails?.eventOutcomeID
				&& this.getEventDetails?.eventOutcomeID !== null
			)
			&& !this.hasUserManualChange;
	}

	@Watch("eventOutcome")
	private validate(): boolean {
		this.outcomeSelectionValid = !(this.eventOutcome != null && this.eventOutcome.childOutcomes && this.eventOutcome.childOutcomes.length > 0);
		let response: EventOutcomeAndNote = {
			valid: (this.isOutcomeNoteValid && this.outcomeSelectionValid),
			eventOutcomeNote: this.outcomeNote,
			eventOutcomeId: this.eventOutcome?.eventOutcomeID,
			eventOutcomePath: this.outcomeSelectionPath
		};
		this.$emit("on-change", response);

		return (this.isOutcomeNoteValid && this.outcomeSelectionValid);
	}


	@Watch("getEventDetails", { deep: true })
	private async onEventDetailsChange(): Promise<void> {
		if (!this.isPersistEventOutcomeEnabled) {
			return;
		}
		// When the event details change (e.g., when outcome ID/Note changes),
		// we need to update the outcome path and note and emit these changes for parent components.
		await this.loadOutcomesForEvent();
		this.setOutcomeNote();
		this.validate();
	}

	private buildOutcomePath(): void {
		const eventOutcomeID = this.getEventDetails.eventOutcomeID;
		if (eventOutcomeID) {
			let idToFind = eventOutcomeID;
			for (let outcome of this.outcomeTree) {
				if (this.checkOutcomeInPath(outcome, idToFind)) {
					this.outcomeSelectionPath = [outcome, ...this.outcomeSelectionPath];
					return;
				}
			}
		}
		this.outcomeSelectionPath = [];
	}

	private setOutcomeNote(): void {
		if (this.eventNote) {
			this.outcomeNote = this.eventNote;
		} else {
			this.outcomeNote = this.getEventDetails?.eventOutcomeNote ?? "";
		}
	}

	private checkOutcomeInPath(outcome: any, idToFind: number): boolean {
		if (outcome.eventOutcomeID == idToFind) {
			return true;
		}

		if (!!outcome.childOutcomes && outcome.childOutcomes.length > 0) {
			for (let child of outcome.childOutcomes) {
				if (this.checkOutcomeInPath(child, idToFind)) {
					this.outcomeSelectionPath = [child, ...this.outcomeSelectionPath];
					return true;
				}
			}
		}
		return false;
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	private get showExistingOutcomeWarning(): boolean {
		return this.outcomeSelectionPath.length === 0 && this.getEventDetails?.eventOutcomeID != null;
	}

	private get existingEventOutcomeTitle(): string | null {
		if (!this.getEventDetails?.eventOutcomeID) {
			return null;
		}
		return this.getEventDetails?.outcomeTitle ?? ("(Could not retrieve outcome title)")
	}

	private get isVirtualAssistEnabled(): boolean {
		return this.getFeature(["AI", "VirtualAssist", "NotificationModal"]) && this.getPermissions.canUseVirtualAssistSummary;
	}

	private onSummaryGenerated(summary: string): void {
		this.outcomeNote = summary;
	}

	private get eventOutcome(): EventOutcome | null {
		return this.outcomeSelectionPath.length === 0
			? null
			: this.outcomeSelectionPath[this.outcomeSelectionPath.length - 1];
	}

	private get isOutcomeNoteValid(): boolean {
		return this.outcomeNote.length <= 5000;
	}

	private get noteStatus(): string {
		let status = `${this.outcomeNote.length} | 5000`;

		if (this.outcomeNote.length > 5000) {
			status = status + ` - Exceeds maximum allowed length`;
		}

		return status;
	}

	private onManualChange(value: EventOutcome[]): void {
		if (!value) {
			return;
		}

		const lastElement = value[value.length - 1];

		if (lastElement.childOutcomes && lastElement.childOutcomes.length > 0) {
			return;
		}

		if (lastElement.eventOutcomeID !== this.getEventDetails?.eventOutcomeID) {
			this.hasUserManualChange = true;
		}
	}

	private rebuildOutcomePath(): void {
		this.outcomeSelectionPath = [];
		this.buildOutcomePath();
	}
}
